/**
 * Options for opening a window
 */
export interface WindowOpenOptions {
    /**
   * The target window name or special values like '_blank', '_self', '_parent', '_top'
   */
    target?: '_blank' | '_self' | '_parent' | '_top' | string;

    /**
   * Window features as a comma-separated list
   * Common features include: width, height, top, left, menubar, toolbar, location, status, scrollbars, resizable, noopener, noreferrer
   */
    features?: string;

    /**
   * Whether to replace the current entry in the browser's history
   */
    replace?: boolean;
}

/**
 * Checks whether a URL is safe to navigate to.
 *
 * Allows absolute http:/https: URLs (including cross-origin ones) and
 * same-document relative paths; rejects other schemes such as javascript:,
 * data: and vbscript:.
 *
 * @param url - The URL to validate
 * @returns true if the URL can safely be assigned to window.location
 */
export function isSafeNavigationUrl(url: string): boolean {
    try {
        const { protocol } = new URL(url, window.location.origin);
        return protocol === 'https:' || protocol === 'http:';
    } catch {
        return false;
    }
}

/**
 * Opens a URL in a new window or the current window based on the provided options
 *
 * @param url - The URL to open
 * @param options - Configuration options for opening the window
 * @returns The window object that was opened, or null if the window couldn't be opened
 */
export function openWindow(url: string = '', options: WindowOpenOptions = {
    target: '_blank',
    features: 'noopener,noreferrer',
    replace: false,
}): Window | null {
    const {
        target, features, replace,
    } = options;

    // If target is _self, use location.href instead of window.open
    if (target === '_self') {
        // Never assign javascript:/data: style URLs to the current document
        if (!isSafeNavigationUrl(url)) {
            return null;
        }
        if (replace) {
            window.location.replace(url);
        } else {
            window.location.href = url;
        }
        return null;
    }

    // For other targets, use window.open
    return window.open(url, target, features);
}
